import { TestLocators } from 'constants/Locators';

import { CustomLink, MUI, ThemeMode } from 'ci-esi-web-shared';
import copy from 'copy-to-clipboard';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomHeaderMenu } from 'app/components/common/CustomHeaderMenu';
import { formatTestLocator } from 'helpers/formatters';
import { translations } from 'locales/i18n';
import { log } from 'services/log';
import { LogEvent } from 'services/LogEvent';
import { LogSource } from 'services/LogSource';
import { colors } from 'styles/theme/colors';

import { Contact } from './Contact';

export interface HelpProps {
  anchorEl: HTMLElement | null;
  onClose(): void;
}

const helpItems = [
  { info: translations.HELP.LINKS.ABOUT_IMMERSE },
  {
    info: {
      ...translations.HELP.LINKS.DOCUMENTATION,
      URL: process.env.REACT_APP_IMPACT_DOCUMENTATION_URL as string,
    },
  },
  { info: translations.HELP.LINKS.DOCUMENTATION, divider: true },
  {
    info: {
      ...translations.HELP.LINKS.LEARNING_CENTER,
      URL: process.env.REACT_APP_LEARNING_CENTER_URL as string,
    },
  },
  { info: translations.HELP.LINKS.CUSTOMER_CARE_WEBSITE },
];

export function Help({ anchorEl, onClose }: HelpProps) {
  const { t } = useTranslation();
  const theme = MUI.useTheme();
  const isDarkMode = theme.palette.mode === ThemeMode.DARK;
  const [copyStatus, setCopyStatus] = useState(t(translations.OPERATIONS.COPY));
  const contacts = useMemo(
    () => Object.keys(translations.HELP.CONTACTS).map((key, index) => <Contact index={index} key={key} />),
    [],
  );

  const handleCopyEmail = useCallback(() => {
    copy(t(translations.HELP.EMAIL_CUSTOMER_CARE.EMAIL));
    setCopyStatus(t(translations.OPERATIONS.COPIED));
    setTimeout(() => setCopyStatus(t(translations.OPERATIONS.COPY)), 2000);
  }, [t]);

  const handleClickOnLink = (itemName: string) => () => {
    log.info({
      event: LogEvent.CLICK,
      source: LogSource.HELP_MENU,
      itemName: itemName,
    });
  };

  return (
    <CustomHeaderMenu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      popperPlacement="bottom-start"
      menuListTestId={TestLocators.Header_Help_MenuList}
    >
      {helpItems.map(({ info, divider }) => {
        return divider ? (
          <MUI.Divider key={`divider_${info.TITLE}`} />
        ) : (
          <MUI.MenuItem
            key={info.TITLE}
            onClick={onClose}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              height: 36,
              padding: '0 16px',
              fontWeight: theme.typography.fontWeightRegular,
            }}
          >
            <CustomLink
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                '&:hover': { color: `${isDarkMode ? colors.baseGrey10 : colors.baseGrey80}!important` },
              }}
              onClick={handleClickOnLink(t(info.TITLE))}
              href={t(info.URL)}
              text={t(info.TITLE)}
              target="_blank"
              data-testid={formatTestLocator(TestLocators.Header_Help_MenuItem, t(info.TITLE))}
            />
          </MUI.MenuItem>
        );
      })}
      <MUI.MenuItem
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          height: 36,
          padding: '0 16px',
          fontWeight: theme.typography.fontWeightRegular,
        }}
      >
        <CustomLink
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            '&:hover': { color: `${isDarkMode ? colors.baseGrey10 : colors.baseGrey80}!important` },
          }}
          onClick={handleClickOnLink(t(translations.HELP.EMAIL_CUSTOMER_CARE.TITLE))}
          href={`mailto:${t(translations.HELP.EMAIL_CUSTOMER_CARE.EMAIL)}`}
          text={t(translations.HELP.EMAIL_CUSTOMER_CARE.TITLE)}
          data-testid={formatTestLocator(
            TestLocators.Header_Help_MenuItem,
            t(translations.HELP.EMAIL_CUSTOMER_CARE.TITLE),
          )}
        />
        <MUI.Typography
          onClick={handleCopyEmail}
          variant="h4"
          sx={{ color: colors.baseBlue55, cursor: 'pointer', marginLeft: '16px' }}
        >
          {copyStatus}
        </MUI.Typography>
      </MUI.MenuItem>
      <MUI.Divider />
      <MUI.Box>{contacts}</MUI.Box>
    </CustomHeaderMenu>
  );
}
